import { useEffect, useState, useCallback } from "react";
import { whatsappService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Copy, Check, Send, MessageSquarePlus, Webhook, ShieldCheck, Zap,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const EMPTY_CFG = {
  mode: "sandbox", phoneNumberId: "", wabaId: "", businessPhone: "",
  accessToken: "", appSecret: "", verifyToken: "", apiVersion: "v21.0",
  autoReply: { enabled: false, message: "" },
};

export default function WhatsAppConfigModal({ channel, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY_CFG);
  const [copied, setCopied] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [simFrom, setSimFrom] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await whatsappService.getConfig(channel.id);
      setInfo(d);
      const c = d.config || {};
      setForm({
        mode: c.mode || "sandbox",
        phoneNumberId: c.phoneNumberId || "",
        wabaId: c.wabaId || "",
        businessPhone: c.businessPhone || "",
        accessToken: "", // never prefill secrets; blank keeps existing
        appSecret: "",
        verifyToken: c.verifyToken || "",
        apiVersion: c.apiVersion || "v21.0",
        autoReply: { enabled: c.autoReply?.enabled || false, message: c.autoReply?.message || "" },
      });
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [channel.id]);

  useEffect(() => { load(); }, [load]);

  const webhookUrl = info ? `${BACKEND_URL}${info.webhookPath}` : "";
  const hasSecret = (k) => (info?.config?.[k] || "").startsWith("•");

  const copyWebhook = () => {
    navigator.clipboard?.writeText(webhookUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const save = async () => {
    if (form.mode === "live" && (!form.phoneNumberId || (!form.accessToken && !hasSecret("accessToken")) || (!form.appSecret && !hasSecret("appSecret")))) {
      return toast.error("Live mode needs Phone Number ID, Access Token and App Secret");
    }
    setSaving(true);
    try {
      const d = await whatsappService.connect(channel.id, form);
      toast.success(d.configured ? "WhatsApp connected" : "Configuration saved");
      setInfo(d); onSaved?.(); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a recipient phone number");
    setBusy("test");
    try {
      const d = await whatsappService.test(channel.id, { to: testTo.trim() });
      toast.success(d.sandbox ? "Test sent (sandbox)" : "Test message sent");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const simulate = async () => {
    if (!simFrom.trim()) return toast.error("Enter a sender phone number");
    setBusy("sim");
    try {
      const d = await whatsappService.simulateInbound(channel.id, { fromPhone: simFrom.trim(), name: "Sandbox Contact" });
      toast.success(`Inbound simulated (${d.inbound} msg${d.autoReplies ? ", auto-reply sent" : ""})`);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card text-foreground rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="whatsapp-config-modal">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-green-500/15 text-green-600 grid place-items-center"><MessageSquarePlus className="h-5 w-5" /></div>
            <div>
              <h3 className="text-base font-bold leading-tight">WhatsApp Business — {channel.name}</h3>
              <p className="text-xs text-muted-foreground">{info?.configured ? "Connected via Meta Cloud API" : "Not configured yet"}</p>
            </div>
          </div>
          {info?.configured && <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-500/10 rounded-full px-2.5 py-1"><ShieldCheck className="h-3.5 w-3.5" /> {form.mode === "sandbox" ? "Sandbox" : "Live"}</span>}
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Mode */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">Mode</span>
              <div className="inline-flex rounded-lg border border-border p-0.5">
                {["sandbox", "live"].map((m) => (
                  <button key={m} onClick={() => setForm({ ...form, mode: m })}
                    className={`px-3 py-1 text-xs font-semibold rounded-md capitalize transition-colors ${form.mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                    data-testid={`whatsapp-mode-${m}`}>{m}</button>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">{form.mode === "sandbox" ? "Simulates sends — no live Meta calls" : "Sends via your Meta Cloud API credentials"}</span>
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone Number ID" value={form.phoneNumberId} onChange={(v) => setForm({ ...form, phoneNumberId: v })} testid="whatsapp-phone-number-id" />
              <Field label="WABA ID" value={form.wabaId} onChange={(v) => setForm({ ...form, wabaId: v })} testid="whatsapp-waba-id" />
              <Field label="Business Phone" value={form.businessPhone} onChange={(v) => setForm({ ...form, businessPhone: v })} testid="whatsapp-business-phone" />
              <Field label="Graph API Version" value={form.apiVersion} onChange={(v) => setForm({ ...form, apiVersion: v })} testid="whatsapp-api-version" />
              <Field label="Access Token" type="password" value={form.accessToken} onChange={(v) => setForm({ ...form, accessToken: v })}
                placeholder={hasSecret("accessToken") ? "•••• (saved — leave blank to keep)" : "Permanent token"} testid="whatsapp-access-token" />
              <Field label="App Secret" type="password" value={form.appSecret} onChange={(v) => setForm({ ...form, appSecret: v })}
                placeholder={hasSecret("appSecret") ? "•••• (saved — leave blank to keep)" : "Meta app secret"} testid="whatsapp-app-secret" />
            </div>
            <p className="text-xs text-muted-foreground -mt-3" data-testid="whatsapp-creds-hint">
              {form.mode === "sandbox"
                ? "Sandbox mode — Meta credentials are optional; sends are simulated."
                : (hasSecret("accessToken") || hasSecret("appSecret"))
                  ? "Live Meta credentials are on file (masked). Leave secret fields blank to keep them."
                  : "Live mode requires your Meta Cloud API Access Token and App Secret."}
            </p>

            {/* Webhook */}
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2"><Webhook className="h-3.5 w-3.5" /> Webhook configuration (Meta dashboard)</div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Callback URL</label>
                  <div className="flex gap-2">
                    <input readOnly value={webhookUrl} className="ciq-input text-xs font-mono" data-testid="whatsapp-webhook-url" />
                    <button onClick={copyWebhook} className="shrink-0 px-3 rounded-lg border border-border hover:bg-muted text-sm flex items-center gap-1" data-testid="whatsapp-webhook-copy">
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Verify Token</label>
                  <input readOnly value={form.verifyToken || "(generated on save)"} className="ciq-input text-xs font-mono" data-testid="whatsapp-verify-token" />
                </div>
              </div>
            </div>

            {/* Auto-reply */}
            <div className="rounded-xl border border-border p-4">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={form.autoReply.enabled} onChange={(e) => setForm({ ...form, autoReply: { ...form.autoReply, enabled: e.target.checked } })} data-testid="whatsapp-autoreply-toggle" />
                <Zap className="h-4 w-4 text-amber-500" /> Auto-reply to inbound messages
              </label>
              {form.autoReply.enabled && (
                <textarea value={form.autoReply.message} onChange={(e) => setForm({ ...form, autoReply: { ...form.autoReply, message: e.target.value } })}
                  rows={2} placeholder="Auto-reply message…" className="ciq-input text-sm mt-2" data-testid="whatsapp-autoreply-message" />
              )}
            </div>

            {/* Save */}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Close</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="whatsapp-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save & Connect
              </button>
            </div>

            {/* Tools (after configured) */}
            {info?.configured && (
              <div className="border-t border-border pt-5 space-y-4">
                <div className="flex items-end gap-2">
                  <div className="flex-1"><Field label="Send test message to" value={testTo} onChange={setTestTo} placeholder="+15551234567" testid="whatsapp-test-to" /></div>
                  <button onClick={sendTest} disabled={busy === "test"} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted flex items-center gap-2 disabled:opacity-60" data-testid="whatsapp-test-btn">
                    {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test
                  </button>
                </div>
                {form.mode === "sandbox" && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1"><Field label="Simulate inbound from (sandbox)" value={simFrom} onChange={setSimFrom} placeholder="+15559876543" testid="whatsapp-sim-from" /></div>
                    <button onClick={simulate} disabled={busy === "sim"} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted flex items-center gap-2 disabled:opacity-60" data-testid="whatsapp-sim-btn">
                      {busy === "sim" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />} Simulate
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="ciq-input text-sm" data-testid={testid} />
    </div>
  );
}
